/**
 * BooleanQuiz Student Renderer
 * Legacy-style UI: two circular O/X buttons flanking a center feedback display area
 * with Lottie character animation (queda) that reacts to hover/selection.
 * Animation logic matches legacy BooleanAnimation.jsx exactly.
 */

import { useState, useRef, useEffect } from 'react'
import { cn } from '../../../lib/utils'
import { KatexText, SubmitButton, QuizSolveDescription, QuizMedia, QuizTypeBadge, QuizResultToggle, useQuizStyleVariant, type QuizResultToggleValue } from '../shared'
import type { BooleanQuizData, QuizSolveResult } from '../../../types/legacyQuiz'
import { formatText } from '../../../utils/quiz-parser'
import { useBooleanQuiz } from '../hooks'
import { useTranslation } from 'react-i18next'
import { motion } from 'framer-motion'
import Lottie from 'lottie-react'
import type { LottieRefCurrentProps } from 'lottie-react'
import quedaAnim from '../boolean/assets/queda-ox-60.json'
import dotAnim from '../boolean/assets/dot.json'

// Lottie segment constants (matching legacy BooleanAnimation.jsx)
const SEG_START = 0
const SEG_MID = 30
const SEG_END = 60

interface BooleanStudentProps {
  quizId: string | number
  quiz: BooleanQuizData
  onSolve?: (quizId: string | number, answers: string[], vars: Record<string, unknown>, elapsedTime: number) => void
  solveResult?: QuizSolveResult | null
  showReallyTrue?: boolean
  isViewSolved?: boolean
  initialAnswers?: string[]
  submitLabel?: string
  hideSubmitButton?: boolean
  onAnswerChange?: (answers: string[]) => void
  disableSound?: boolean
  difficultyLevel?: number | null
  /** G3 — true면 풀이 후 해설(QuizSolveDescription)을 숨긴다(정오답 피드백은 유지). 기본 false=현행 노출. */
  hideExplanation?: boolean
  /** true면 결과 토글은 유지하되 풀이/해설 UI만 숨긴다. */
  hideSolveDescription?: boolean
  /** G4 — true면 유형별 노출표에 따라 발문/내용/문제 이미지를 흐린다. */
  dimChoiceText?: boolean
}

export function BooleanStudent({
  quizId,
  quiz,
  onSolve,
  solveResult,
  showReallyTrue = false,
  isViewSolved = false,
  initialAnswers: initialAnswersProp,
  submitLabel,
  hideSubmitButton = false,
  onAnswerChange,
  disableSound: _disableSound = false,
  difficultyLevel,
  hideExplanation = false,
  hideSolveDescription = false,
  dimChoiceText = false,
}: BooleanStudentProps) {
  const { t } = useTranslation('quiz')
  const styleVariant = useQuizStyleVariant()

  const [hoverO, setHoverO] = useState(false)
  const [hoverX, setHoverX] = useState(false)
  const lottieRef = useRef<LottieRefCurrentProps>(null)
  const mountedRef = useRef(false)
  // Track which "side" the character is currently at: 'left' | 'center' | 'right'
  const posRef = useRef<'left' | 'center' | 'right'>('center')

  const {
    selected,
    isSolved,
    canSubmit,
    correctAnswer,
    shouldShowCorrectAnswers,
    isShowingCorrectAnswers,
    setIsShowingCorrectAnswers,
    handleSelect,
    handleSubmit,
  } = useBooleanQuiz({
    quizId,
    quiz,
    onSolve,
    solveResult,
    showReallyTrue,
    isViewSolved,
    initialAnswers: initialAnswersProp,
    onAnswerChange,
  })

  const isInteractive = !isSolved && !isViewSolved
  const displayedSelection = shouldShowCorrectAnswers ? correctAnswer : selected

  // Initial position: center (frame 30), then mark as mounted
  useEffect(() => {
    lottieRef.current?.goToAndStop(SEG_MID, true)
    requestAnimationFrame(() => { mountedRef.current = true })
  }, [])

  // Hover O button (true) — character looks LEFT toward O
  // lottie-web playSegments: [a, b] where a<b = forward, a>b = backward
  useEffect(() => {
    const anim = lottieRef.current
    if (!anim || !mountedRef.current || selected !== null) return

    if (hoverO) {
      // Center → Left: play 30→0 (backward)
      anim.playSegments([SEG_MID, SEG_START], true)
      posRef.current = 'left'
    } else {
      // Left → Center: play 0→30 (forward)
      anim.playSegments([SEG_START, SEG_MID], true)
      posRef.current = 'center'
    }
  }, [hoverO])

  // Hover X button (false) — character looks RIGHT toward X
  useEffect(() => {
    const anim = lottieRef.current
    if (!anim || !mountedRef.current || selected !== null) return

    if (hoverX) {
      // Center → Right: play 30→60 (forward)
      anim.playSegments([SEG_MID, SEG_END], true)
      posRef.current = 'right'
    } else {
      // Right → Center: play 60→30 (backward)
      anim.playSegments([SEG_END, SEG_MID], true)
      posRef.current = 'center'
    }
  }, [hoverX])

  // Answer selected — animate to final position
  useEffect(() => {
    const anim = lottieRef.current
    if (!anim || displayedSelection === null) return

    if (displayedSelection === true) {
      if (posRef.current === 'left') return // already there
      // Go to left: play current→0
      anim.playSegments([SEG_END, SEG_START], true)
      posRef.current = 'left'
    } else {
      if (posRef.current === 'right') return // already there
      // Go to right: play current→60
      anim.playSegments([SEG_START, SEG_END], true)
      posRef.current = 'right'
    }
  }, [displayedSelection])

  // O button state helpers
  const oIsSelected = displayedSelection === true
  const oIsCorrectResult = isSolved && correctAnswer === true && (shouldShowCorrectAnswers || selected === true)
  const oIsWrongResult = isSolved && !shouldShowCorrectAnswers && selected === true && correctAnswer === false

  // X button state helpers
  const xIsSelected = displayedSelection === false
  const xIsCorrectResult = isSolved && correctAnswer === false && (shouldShowCorrectAnswers || selected === false)
  const xIsWrongResult = isSolved && !shouldShowCorrectAnswers && selected === false && correctAnswer === true

  // --- O button classes ---
  const oButtonClass = cn(
    'w-[88px] h-[88px] rounded-full cursor-pointer select-none transition-all duration-150',
    'active:translate-y-2 active:[box-shadow:unset] active:border-b-0',
    (isSolved || isViewSolved) && 'pointer-events-none',
    oIsCorrectResult && 'bg-lc-mint-900 [box-shadow:unset] translate-y-2',
    oIsWrongResult && 'bg-lc-red-900 [box-shadow:unset] translate-y-2',
    !isSolved && oIsSelected && 'bg-lc-orange-900 [box-shadow:unset] translate-y-2',
    isSolved && !oIsCorrectResult && !oIsWrongResult && !oIsSelected && 'bg-lc-gray-200 [box-shadow:0px_6px_0px_#BBBBBB,0px_12px_0px_rgba(16,16,16,0.1)]',
    !isSolved && !oIsSelected && 'bg-lc-lightblue-900 [box-shadow:0px_6px_0px_#2E96CE,0px_12px_0px_rgba(16,16,16,0.1)] hover:bg-lc-lightblue-600 hover:[box-shadow:0px_6px_0px_#55c3ff,0px_12px_0px_rgba(16,16,16,0.1)]',
  )

  // --- X button classes ---
  const xButtonClass = cn(
    'w-[88px] h-[88px] rounded-full cursor-pointer select-none transition-all duration-150',
    'active:translate-y-2 active:[box-shadow:unset] active:border-b-0',
    (isSolved || isViewSolved) && 'pointer-events-none',
    xIsCorrectResult && 'bg-lc-mint-900 [box-shadow:unset] translate-y-2',
    xIsWrongResult && 'bg-lc-red-900 [box-shadow:unset] translate-y-2',
    !isSolved && xIsSelected && 'bg-lc-orange-900 [box-shadow:unset] translate-y-2',
    isSolved && !xIsCorrectResult && !xIsWrongResult && !xIsSelected && 'bg-lc-gray-200 [box-shadow:0px_6px_0px_#BBBBBB,0px_12px_0px_rgba(16,16,16,0.1)]',
    !isSolved && !xIsSelected && 'bg-lc-lightblue-900 [box-shadow:0px_6px_0px_#2E96CE,0px_12px_0px_rgba(16,16,16,0.1)] hover:bg-lc-lightblue-600 hover:[box-shadow:0px_6px_0px_#55c3ff,0px_12px_0px_rgba(16,16,16,0.1)]',
  )

  // --- Center display: text + background (matching legacy BooleanQuiz.jsx exactly) ---
  const completed = isSolved

  // Center background
  const centerBgClass = cn(
    'w-[162px] h-[88px] flex items-center justify-center rounded-3xl relative z-10',
    !completed && selected === null && 'bg-lc-lightblue-200',
    !completed && selected !== null && 'bg-lc-orange-200',
    !shouldShowCorrectAnswers && completed && solveResult?.correct === false && 'bg-lc-red-200',
    (shouldShowCorrectAnswers || (completed && solveResult?.correct === true)) && 'bg-lc-mint-200',
  )

  // Center text content — matching legacy ternary chain exactly
  const renderCenterText = () => {
    if (shouldShowCorrectAnswers) {
      return <span className="font-bold text-[32px] text-lc-mint-900">정답</span>
    }

    // Show "맞아요" when: (no answer or X selected) AND hovering O, OR O selected and not hovering X
    const showTrue =
      ((selected === null || selected === false) && hoverO) ||
      (selected === true && !hoverX)

    // Show "틀려요" when: (no answer or O selected) AND hovering X, OR X selected and not hovering O
    const showFalse =
      ((selected === null || selected === true) && hoverX) ||
      (selected === false && !hoverO)

    if (showTrue) {
      const textColor = cn(
        'font-bold text-[32px]',
        hoverO && selected === null && 'text-lc-lightblue-900',
        (selected === true || hoverO) && !completed && 'text-lc-orange-900',
        completed && solveResult?.correct === true && 'text-lc-mint-900',
      )
      return <span className={textColor}>{t('types.boolean.correct')}</span>
    }

    if (showFalse) {
      const textColor = cn(
        'font-bold text-[32px]',
        hoverX && selected === null && 'text-lc-lightblue-900',
        (selected === false || hoverX) && !completed && 'text-lc-orange-900',
        completed && solveResult?.correct === false && 'text-lc-red-900',
      )
      return <span className={textColor}>{t('types.boolean.incorrect')}</span>
    }

    // Default: animated dots (matching legacy dot.json Lottie — 55x10 native)
    return <Lottie animationData={dotAnim} loop autoplay style={{ width: 55, height: 10 }} />
  }

  return (
    <div className="space-y-6">
      {/* 배지 영역 */}
      <motion.div
        initial={{ opacity: 0, y: -10 }}
        animate={{ opacity: 1, y: 0 }}
        className="space-y-2"
      >
        <QuizTypeBadge
          quizId={quizId}
          typeLabel={t('types.boolean.label')}
          difficultyLevel={difficultyLevel}
          variant="student"
        />
      </motion.div>

      {/* 문제 제목 */}
      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ delay: 0.1 }}
        className={cn(
          styleVariant !== 'lc' && 'p-6 bg-linear-to-br to-gray-50 rounded-2xl from-slate-50',
          styleVariant !== 'lc' && 'border border-gray-100 shadow-2xs',
          'text-2xl font-medium leading-relaxed text-gray-900'
        )}
      >
        <span data-quiz-title-text className={cn(dimChoiceText && 'opacity-[0.05]')}>
          <KatexText text={quiz.title} />
        </span>
        {quiz.problem && (
          <div data-quiz-problem-text className={cn('mt-4 text-xl', dimChoiceText && 'opacity-[0.05]')}>
            {formatText(quiz.problem, `${quizId}-problem`, quiz.additionalImages ?? undefined)}
          </div>
        )}
        <QuizMedia
          image={quiz.image}
          imageMobile={quiz.imageMobile}
          desmos={quiz.desmos}
          vars={quiz.vars}
          className={cn(dimChoiceText && 'opacity-[0.05]')}
        />
      </motion.div>

      {/* O / Character+Center / X layout */}
      <div
        className="flex gap-10 justify-center items-end mt-8"
        role="radiogroup"
        aria-label={t('types.boolean.selectLabel')}
      >
        {/* O Button */}
        <button
          role="radio"
          aria-checked={oIsSelected}
          onClick={() => isInteractive && handleSelect(true)}
          onMouseEnter={() => isInteractive && !oIsSelected && setHoverO(true)}
          onMouseLeave={() => setHoverO(false)}
          className={oButtonClass}
          aria-disabled={!isInteractive}
        >
          <svg
            width="44"
            height="44"
            viewBox="0 0 44 44"
            fill="none"
            xmlns="http://www.w3.org/2000/svg"
            className="mx-auto"
          >
            <circle cx="22" cy="22" r="14" stroke="white" strokeWidth="5" fill="none" />
          </svg>
        </button>

        {/* Center column: Character + Answer display */}
        <div className="flex flex-col justify-center items-center text-gray-700">
          {/* Lottie character — positioned above center box with overlap */}
          <div className="-mb-[64px] z-0">
            <Lottie
              lottieRef={lottieRef}
              animationData={quedaAnim}
              loop={false}
              autoplay={false}
              style={{ width: 100, height: 150 }}
            />
          </div>

          {/* Center display area */}
          <div className={centerBgClass}>
            {renderCenterText()}
          </div>
        </div>

        {/* X Button */}
        <button
          role="radio"
          aria-checked={xIsSelected}
          onClick={() => isInteractive && handleSelect(false)}
          onMouseEnter={() => isInteractive && !xIsSelected && setHoverX(true)}
          onMouseLeave={() => setHoverX(false)}
          className={xButtonClass}
          aria-disabled={!isInteractive}
        >
          <svg
            width="44"
            height="44"
            viewBox="0 0 44 44"
            fill="none"
            xmlns="http://www.w3.org/2000/svg"
            className="mx-auto"
          >
            <line x1="12" y1="12" x2="32" y2="32" stroke="white" strokeWidth="5" strokeLinecap="round" />
            <line x1="32" y1="12" x2="12" y2="32" stroke="white" strokeWidth="5" strokeLinecap="round" />
          </svg>
        </button>
      </div>

      {/* 정답 확인 버튼 */}
      {!isSolved && !isViewSolved && !hideSubmitButton && (
        <SubmitButton
          onClick={handleSubmit}
          disabled={!canSubmit}
          label={submitLabel}
        />
      )}

      {/* 풀이 결과 토글 (정답과 풀이 / 내가 쓴 답) */}
      {isSolved && !hideExplanation && (
        <QuizResultToggle
          value={isShowingCorrectAnswers ? 'correct-and-explanation' : 'my-answer'}
          onChange={(next: QuizResultToggleValue) => setIsShowingCorrectAnswers(next === 'correct-and-explanation')}
        />
      )}

      {/* 해설은 '정답과 풀이' 일 때만 노출 */}
      {isSolved && isShowingCorrectAnswers && !hideExplanation && !hideSolveDescription && (
        <QuizSolveDescription
          description={solveResult?.description ?? undefined}
          commentaryImages={quiz.commentaryImages}
          vars={quiz.vars}
          id={`${quizId}-desc`}
          actionVisible={false}
        />
      )}
    </div>
  )
}
